import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarX, AlertTriangle, X } from "lucide-react";
import { formatRange, dayLabel, romeTodayKey } from "@/lib/constants";

const todayKey = romeTodayKey;

function daysOf(u) {
  const out = [];
  const d = new Date(`${u.start}T12:00:00`);
  const end = new Date(`${u.end}T12:00:00`);
  for (; d <= end; d.setDate(d.getDate() + 1)) out.push(d.toLocaleDateString("sv-SE"));
  return out.filter((x) => x >= todayKey());
}

export function RemoveDialog({ unav, onClose, onDone }) {
  const days = unav ? daysOf(unav) : [];
  const [sel, setSel] = useState([]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (unav) { setSel(daysOf(unav)); setStep(1); } }, [unav]);
  const toggle = (d) => setSel((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d]));
  const send = async () => {
    setBusy(true);
    try { await api.post("/unavailability/me/remove", { dates: sel }); toast.success("Fatto: il manager è stato avvisato"); onClose(); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!unav} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display">Torno disponibile</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Scegli i giorni in cui non sei più indisponibile. Il manager riceverà notifica ed email.</DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
            {days.map((d) => (
              <label key={d} data-testid={`unav-remove-day-${d}`} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-800/60 px-3 py-2 text-sm cursor-pointer">
                <Checkbox checked={sel.includes(d)} onCheckedChange={() => toggle(d)} /> {dayLabel(d)}
              </label>
            ))}
          </div>
        ) : (
          <div data-testid="unav-remove-confirm-box" className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100">
            Confermi di tornare <b>disponibile</b> per: <b>{[...sel].sort().map((d) => dayLabel(d)).join(", ")}</b>?
          </div>
        )}
        <DialogFooter className="gap-2">
          {step === 1 ? (
            <Button data-testid="unav-remove-next-btn" disabled={!sel.length} onClick={() => setStep(2)} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">Avanti ({sel.length})</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} data-testid="unav-remove-back-btn" className="border-slate-700">Indietro</Button>
              <Button data-testid="unav-remove-send-btn" disabled={busy} onClick={send} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">Sì, conferma</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UnavDialog({ open, onOpenChange, onSent, initialDate = "" }) {
  const [form, setForm] = useState({ start: "", end: "", reason: "" });
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm({ start: initialDate, end: "", reason: "" }); setStep(1); } }, [open, initialDate]);
  const end = form.end || form.start;
  const valid = form.start && form.start >= todayKey() && end >= form.start;
  const send = async () => {
    setBusy(true);
    try { await api.post("/unavailability/me", { ...form, end }); toast.success("Inviata al manager: in attesa di approvazione"); onOpenChange(false); onSent(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display">Segnala indisponibilità</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Il manager dovrà approvarla prima che diventi effettiva.</DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Dal</Label><Input type="date" lang="it" min={todayKey()} data-testid="unav-start" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="bg-slate-800 border-slate-700" /></div>
              <div className="space-y-1"><Label>Al (opzionale)</Label><Input type="date" lang="it" min={form.start || todayKey()} data-testid="unav-end" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="bg-slate-800 border-slate-700" /></div>
            </div>
            <div className="space-y-1"><Label>Motivo (opzionale)</Label><Input data-testid="unav-reason" maxLength={120} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="es. ferie, visita medica" className="bg-slate-800 border-slate-700" /></div>
          </div>
        ) : (
          <div data-testid="unav-confirm-box" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-100 flex gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-400" />
            <span>Confermi di <b>NON essere disponibile</b> {form.start === end ? "il" : "nel periodo"} <b>{formatRange(form.start, end)}</b>?{form.reason && <> Motivo: {form.reason}.</>}</span>
          </div>
        )}
        <DialogFooter className="gap-2">
          {step === 1 ? (
            <Button data-testid="unav-next-btn" disabled={!valid} onClick={() => setStep(2)} className="bg-red-500 hover:bg-red-600 text-white font-semibold">Avanti</Button>
          ) : (
            <>
              <Button variant="outline" data-testid="unav-back-btn" onClick={() => setStep(1)} className="border-slate-700">Indietro</Button>
              <Button data-testid="unav-send-btn" disabled={busy} onClick={send} className="bg-red-500 hover:bg-red-600 text-white font-semibold">Sì, confermo e invio</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TechUnavPanel({ onChanged, reloadKey = 0 }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => {
    api.get("/unavailability/me/requests").then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [reloadKey]);
  const refresh = () => { load(); onChanged?.(); };
  const withdraw = async (id) => {
    try { await api.delete(`/unavailability/me/requests/${id}`); toast.success("Segnalazione ritirata"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="mb-4 space-y-2" data-testid="tech-unav-panel">
      <Button data-testid="tech-unav-open-btn" onClick={() => setOpen(true)} variant="outline" className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10">
        <CalendarX className="w-4 h-4 mr-2" /> Segnala indisponibilità
      </Button>
      {items.map((u) => (
        <div key={u.id} data-testid={`tech-unav-req-${u.id}`} className="flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/10 px-3 py-2 text-sm">
          <span className="flex-1 text-orange-200">In approvazione: <b>{formatRange(u.start, u.end)}</b>{u.reason && <span className="text-orange-300/80"> · {u.reason}</span>}</span>
          <Button size="sm" variant="ghost" data-testid={`tech-unav-withdraw-${u.id}`} onClick={() => withdraw(u.id)} className="h-7 px-2 text-orange-200 hover:text-white"><X className="w-4 h-4 mr-1" />Ritira</Button>
        </div>
      ))}
      <UnavDialog open={open} onOpenChange={setOpen} onSent={refresh} />
    </div>
  );
}
